import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Camera, X, ScanLine, Loader2 } from "lucide-react";

const BarcodeScanner = ({ isOpen, onClose, onScan, title = "Scan Barcode" }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Get available cameras
      Html5Qrcode.getCameras()
        .then((devices) => {
          if (devices && devices.length) {
            setCameras(devices);
            // Prefer back camera
            const backCamera = devices.find(
              (d) => d.label.toLowerCase().includes("back") || 
                     d.label.toLowerCase().includes("rear") ||
                     d.label.toLowerCase().includes("environment")
            );
            setSelectedCamera(backCamera?.id || devices[0].id);
          } else {
            setError("No cameras found on this device");
          }
        })
        .catch((err) => {
          setError("Could not access camera. Please check permissions.");
        });
    }

    return () => {
      stopScanning();
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selectedCamera && !isScanning) {
      startScanning();
    }
  }, [selectedCamera, isOpen]);

  const startScanning = async () => {
    if (!selectedCamera || isScanning) return;

    setError(null);
    setIsScanning(true);

    try {
      html5QrCodeRef.current = new Html5Qrcode("barcode-scanner-region");
      
      await html5QrCodeRef.current.start(
        selectedCamera,
        {
          fps: 10,
          qrbox: { width: 280, height: 180 },
          aspectRatio: 1.5,
        },
        (decodedText, decodedResult) => {
          // Successfully scanned
          handleScanSuccess(decodedText, decodedResult);
        },
        (errorMessage) => {
          // Scan error - ignore, keep scanning
        }
      );
    } catch (err) {
      setError("Could not start camera. Please try again.");
      setIsScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  const handleScanSuccess = async (decodedText, decodedResult) => {
    // Stop scanning after successful scan
    await stopScanning();
    
    // Parse the barcode data
    const parsedData = parseBarcode(decodedText, decodedResult.result.format?.formatName);
    
    // Call the onScan callback with parsed data
    onScan(parsedData);
    onClose();
  };

  const parseBarcode = (text, format) => {
    const data = {
      raw: text,
      format: format || "UNKNOWN",
      parsed: {}
    };

    // Try to parse as BCBP (Boarding Card Bar Code) - IATA standard
    if (text.startsWith("M1") || text.startsWith("M2")) {
      data.type = "boarding_pass";
      data.parsed = parseBoardingPass(text);
    }
    // Try to parse as URL
    else if (text.startsWith("http://") || text.startsWith("https://")) {
      data.type = "url";
      data.parsed = { url: text };
    }
    // Try to parse as JSON
    else if (text.startsWith("{") || text.startsWith("[")) {
      try {
        data.parsed = JSON.parse(text);
        data.type = "json";
      } catch {
        data.type = "text";
        data.parsed = { text };
      }
    }
    // Check for common ticket/booking patterns
    else if (text.includes("PNR:") || text.includes("CONF:") || text.includes("REF:")) {
      data.type = "booking_reference";
      const match = text.match(/(PNR|CONF|REF)[:\s]*([A-Z0-9]+)/i);
      if (match) {
        data.parsed = { booking_reference: match[2] };
      }
    }
    // Default to text
    else {
      data.type = "text";
      data.parsed = { text };
    }

    return data;
  };

  const parseBoardingPass = (bcbp) => {
    // IATA BCBP (Bar Coded Boarding Pass) parser
    // Reference: https://www.iata.org/en/programs/passenger/common-use/bar-coded-boarding-pass/
    try {
      const data = {};
      
      // Format code (M = multiple legs)
      const formatCode = bcbp.charAt(0);
      data.legs = parseInt(bcbp.charAt(1)) || 1;
      
      // Passenger name (20 chars, padded with spaces)
      const passengerName = bcbp.substring(2, 22).trim();
      if (passengerName.includes("/")) {
        const [lastName, firstName] = passengerName.split("/");
        data.passenger_name = `${firstName?.trim()} ${lastName?.trim()}`.trim();
      } else {
        data.passenger_name = passengerName;
      }
      
      // Electronic ticket indicator
      data.electronic_ticket = bcbp.charAt(22);
      
      // PNR/Booking Reference (7 chars)
      data.booking_reference = bcbp.substring(23, 30).trim();
      
      // From city (3 chars IATA code)
      data.departure_location = bcbp.substring(30, 33);
      
      // To city (3 chars IATA code)
      data.arrival_location = bcbp.substring(33, 36);
      
      // Airline code (3 chars)
      data.provider = bcbp.substring(36, 39).trim();
      
      // Flight number (5 chars)
      data.flight_number = bcbp.substring(39, 44).trim();
      
      // Julian date (3 chars - day of year)
      const julianDate = bcbp.substring(44, 47);
      if (julianDate && !isNaN(parseInt(julianDate))) {
        const dayOfYear = parseInt(julianDate);
        const year = new Date().getFullYear();
        const date = new Date(year, 0);
        date.setDate(dayOfYear);
        data.departure_date = date.toISOString().split("T")[0];
      }
      
      // Compartment code (1 char)
      data.cabin_class = bcbp.charAt(47);
      
      // Seat number (4 chars)
      data.seat = bcbp.substring(48, 52).trim();
      
      // Check-in sequence (5 chars)
      data.check_in_sequence = bcbp.substring(52, 57).trim();
      
      // Passenger status (1 char)
      data.passenger_status = bcbp.charAt(57);

      return data;
    } catch (err) {
      return { raw: bcbp, error: "Could not parse boarding pass" };
    }
  };

  const handleClose = async () => {
    await stopScanning();
    onClose();
  };

  const switchCamera = () => {
    if (cameras.length > 1) {
      const currentIndex = cameras.findIndex((c) => c.id === selectedCamera);
      const nextIndex = (currentIndex + 1) % cameras.length;
      stopScanning().then(() => {
        setSelectedCamera(cameras[nextIndex].id);
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="glass-card border-white/10 max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-emerald-400" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          {error ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
              <p className="text-red-400 text-sm">{error}</p>
              <Button
                variant="outline"
                className="mt-3 btn-outline"
                onClick={() => {
                  setError(null);
                  startScanning();
                }}
              >
                Try Again
              </Button>
            </div>
          ) : (
            <>
              {/* Scanner viewport */}
              <div className="relative bg-black rounded-lg overflow-hidden">
                <div
                  id="barcode-scanner-region"
                  ref={scannerRef}
                  className="w-full"
                  style={{ minHeight: "300px" }}
                />
                
                {!isScanning && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                    <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                  </div>
                )}

                {/* Scanning overlay */}
                {isScanning && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-44 border-2 border-emerald-400 rounded-lg">
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-lg" />
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-lg" />
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-lg" />
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-lg" />
                      
                      {/* Scanning line animation */}
                      <div className="absolute inset-x-2 h-0.5 bg-emerald-400 animate-scan-line" />
                    </div>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <p className="text-center text-zinc-400 text-sm mt-4">
                Position the barcode within the frame to scan
              </p>

              {/* Camera controls */}
              <div className="flex gap-2 mt-4">
                {cameras.length > 1 && (
                  <Button
                    variant="outline"
                    className="flex-1 btn-outline"
                    onClick={switchCamera}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Switch Camera
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="flex-1 btn-outline text-red-400 border-red-500/30 hover:bg-red-500/10"
                  onClick={handleClose}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>

              {/* Supported formats */}
              <p className="text-center text-zinc-500 text-xs mt-3">
                Supports: QR Code, PDF417, Aztec, DataMatrix, and more
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
